import { DeviceInfo, getDeviceInfo } from './deviceInfo';
import { generateUuid } from '../utils/uuid';

// Type definition for Buffer in browser environment
declare const Buffer: {
  from(array: number[]): { [index: number]: number; length: number };
};

export interface OfflineAction<T = any> {
  id: string;
  type: string;
  payload: T;
  timestamp: number;
  deviceInfo: DeviceInfo;
  userId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'conflict';
  retryCount: number;
  lastAttempt?: number;
  version?: string;
  conflictResolution?: 'server' | 'client' | 'manual';
  resolvedData?: any;
  metadata?: {
    location?: any;
    deviceId?: string;
    appVersion?: string;
  };
}

const OFFLINE_QUEUE_KEY = 'offline_action_queue';
const MAX_RETRIES = 3;

// Conflict resolution strategies for different action types
const CONFLICT_STRATEGIES: Record<string, 'server' | 'client' | 'manual'> = {
  MARK_PANEL_RECEIVED: 'server',
  MARK_HARDWARE_RECEIVED: 'server',
  UPDATE_CARTON_STATUS: 'server',
  SCAN_CARTON: 'server',
  RECEIVE_CARTON: 'server',
  UPDATE_CARTON_RECEIVING_STATUS: 'server',
  // Add more action types as needed
};

class OfflineQueue {
  private isProcessing = false;
  private queueListeners: Array<() => void> = [];

  async addAction<T>(
    type: string, 
    payload: T,
    options: {
      userId?: string;
      location?: any;
      deviceInfo?: Partial<DeviceInfo>;
    } = {}
  ): Promise<OfflineAction<T>> {
    const deviceInfo = options.deviceInfo || await getDeviceInfo();
    
    const action: OfflineAction<T> = {
      id: generateUuid(),
      type,
      payload,
      timestamp: Date.now(),
      deviceInfo: {
        ...deviceInfo,
        ...(options.location && { location: options.location })
      },
      userId: options.userId,
      status: 'pending',
      retryCount: 0,
      metadata: {
        location: options.location,
        deviceId: deviceInfo.deviceId,
        appVersion: deviceInfo.appVersion
      }
    };

    const queue = await this.getQueue();
    queue.push(action);
    await this.saveQueue(queue);
    
    this.notifyQueueChange();
    
    if (navigator.onLine) {
      this.processQueue();
    }

    return action;
  }

  async getQueue(): Promise<OfflineAction[]> {
    if (typeof window === 'undefined') return [];
    
    try {
      const queueJson = localStorage.getItem(OFFLINE_QUEUE_KEY);
      return queueJson ? JSON.parse(queueJson) : [];
    } catch (error) {
      console.error('Error parsing offline queue:', error);
      return [];
    }
  }

  private async saveQueue(queue: OfflineAction[]): Promise<void> {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Error saving offline queue:', error);
      throw new Error('Failed to save offline queue');
    }
  }

  async removeAction(actionId: string): Promise<void> {
    const queue = await this.getQueue();
    const newQueue = queue.filter(action => action.id !== actionId);
    await this.saveQueue(newQueue);
    this.notifyQueueChange();
  }

  async updateAction<T>(actionId: string, updates: Partial<OfflineAction<T>>): Promise<OfflineAction<T> | null> {
    const queue = await this.getQueue();
    const actionIndex = queue.findIndex(a => a.id === actionId);
    
    if (actionIndex === -1) return null;
    
    const updatedAction = {
      ...queue[actionIndex],
      ...updates,
      lastAttempt: updates.status === 'processing' ? Date.now() : queue[actionIndex].lastAttempt
    };
    
    queue[actionIndex] = updatedAction;
    await this.saveQueue(queue);
    this.notifyQueueChange();
    
    return updatedAction as OfflineAction<T>;
  }

  async processQueue(): Promise<void> {
    if (this.isProcessing || !navigator.onLine) return;
    
    this.isProcessing = true;
    
    try {
      const queue = await this.getQueue();
      const pendingActions = queue.filter(
        action => action.status === 'pending' || 
                (action.status === 'failed' && action.retryCount < MAX_RETRIES)
      );

      for (const action of pendingActions) {
        try {
          await this.updateAction(action.id, { status: 'processing' });
          
          const result = await this.processAction(action);
          
          if (result.success) {
            await this.removeAction(action.id);
          } else if (result.conflict) {
            await this.handleConflict(action, result.serverData);
          } else {
            await this.updateAction(action.id, { 
              status: 'failed',
              retryCount: (action.retryCount || 0) + 1
            });
          }
        } catch (error) {
          console.error(`Error processing action ${action.id}:`, error);
          await this.updateAction(action.id, { 
            status: 'failed',
            retryCount: (action.retryCount || 0) + 1
          });
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  private async processAction(action: OfflineAction): Promise<{ 
    success: boolean; 
    conflict?: boolean;
    serverData?: any;
  }> {
    console.log('[OFFLINE_QUEUE] Processing action:', action.type, action.payload);
    
    try {
      // Import API service dynamically to avoid circular dependencies
      const { apiService } = await import('./api');
      
      switch (action.type) {
        case 'MARK_PANEL_RECEIVED':
        case 'MARK_HARDWARE_RECEIVED':
          return await this.processContentReceiving(action, apiService);
          
        case 'SCAN_CARTON':
          return await this.processCartonScan(action, apiService);
          
        case 'RECEIVE_CARTON':
          return await this.processCartonReceive(action, apiService);
          
        case 'UPDATE_CARTON_STATUS':
        case 'UPDATE_CARTON_RECEIVING_STATUS':
          return await this.processCartonStatusUpdate(action, apiService);
          
        default:
          console.warn('[OFFLINE_QUEUE] Unknown action type:', action.type);
          return { success: false };
      }
    } catch (error) {
      console.error('[OFFLINE_QUEUE] Error processing action:', error);
      return { success: false };
    }
  }

  private async processContentReceiving(action: OfflineAction, apiService: any): Promise<{ 
    success: boolean; 
    conflict?: boolean;
    serverData?: any;
  }> {
    const { cartonId, contentCode, projectId, isPanel, isHardware } = action.payload;
    
    try {
      const response = await apiService.receiveCartonContent({
        cartonId,
        contentCode,
        projectId,
        isPanel,
        isHardware
      });
      
      console.log('[OFFLINE_QUEUE] Content receiving response:', response);
      return { success: response.success };
    } catch (error) {
      console.error('[OFFLINE_QUEUE] Content receiving failed:', error);
      return { success: false };
    }
  }

  private async processCartonScan(action: OfflineAction, apiService: any): Promise<{ 
    success: boolean; 
    conflict?: boolean;
    serverData?: any;
  }> {
    const { cartonId, scannedCode } = action.payload;
    
    try {
      const response = await apiService.scanCarton(cartonId, scannedCode);
      console.log('[OFFLINE_QUEUE] Carton scan response:', response);
      return { success: true, serverData: response };
    } catch (error) {
      console.error('[OFFLINE_QUEUE] Carton scan failed:', error);
      return { success: false };
    }
  }

  private async processCartonReceive(action: OfflineAction, apiService: any): Promise<{ 
    success: boolean; 
    conflict?: boolean;
    serverData?: any;
  }> {
    const { cartonId, projectId, remarks } = action.payload;
    
    try {
      const response = await apiService.updateCartonStatus(cartonId, 'received', projectId, remarks);
      console.log('[OFFLINE_QUEUE] Carton receive response:', response);
      return { success: true, serverData: response };
    } catch (error) {
      console.error('[OFFLINE_QUEUE] Carton receive failed:', error);
      return { success: false };
    }
  }

  private async processCartonStatusUpdate(action: OfflineAction, apiService: any): Promise<{ 
    success: boolean; 
    conflict?: boolean;
    serverData?: any;
  }> {
    const { cartonId, status, projectId, remarks } = action.payload;
    
    try {
      const response = await apiService.updateCartonStatus(cartonId, status, projectId, remarks);
      console.log('[OFFLINE_QUEUE] Carton status update response:', response);
      return { success: true, serverData: response };
    } catch (error) {
      console.error('[OFFLINE_QUEUE] Carton status update failed:', error);
      return { success: false };
    }
  }

  private async handleConflict(action: OfflineAction, serverData: any) {
    const strategy = CONFLICT_STRATEGIES[action.type] || 'manual';
    
    switch (strategy) {
      case 'server':
        // Accept server version
        await this.updateAction(action.id, {
          status: 'completed',
          resolvedData: serverData,
          conflictResolution: 'server'
        });
        await this.removeAction(action.id);
        break;
        
      case 'client':
        // Retry with client version
        await this.updateAction(action.id, {
          status: 'pending',
          retryCount: 0,
          conflictResolution: 'client',
          version: serverData.version
        });
        break;
        
      case 'manual':
      default:
        // Mark as conflict for manual resolution
        await this.updateAction(action.id, {
          status: 'conflict',
          conflictResolution: 'manual',
          resolvedData: serverData
        });
        break;
    }
  }

  subscribe(listener: () => void): () => void {
    this.queueListeners.push(listener);
    return () => {
      this.queueListeners = this.queueListeners.filter(l => l !== listener);
    };
  }

  private notifyQueueChange() {
    this.queueListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in queue listener:', error);
      }
    });
  }
}

export const offlineQueue = new OfflineQueue();

// Process queue when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    offlineQueue.processQueue().catch(console.error);
  });
}
